import { useEffect, useRef, useState } from "react"

import AstroMap from "@/assets/img/astro-map/astro-map.svg"
import Moon from "@/assets/img/astro-map/moon.svg"
import { cn } from "@/lib/utils"
import { LINES, MAP_RECT, MOON, PINS, PLANETS, type CosmicMapView } from "./cosmic-map"

/** Track an element's content-box width (sizes the width-fit map frame). */
function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

/**
 * The full-bleed cosmic scanner: the Figma 414×420 frame reproduced 1:1 — the
 * world map, the planetary-line meridians, power-location pins, planet badges, a
 * moon and the stage-1 scan line — driven by a {@link CosmicMapView}. Pure
 * presentation: the scene owns *all* geometry (it measures its region and the
 * camera transform), callers only describe what to show. The frame fills its
 * region by width and cover-zooms (capped) to also fill the height, so the map
 * occupies the available space on any device. The `AstroScanningStep` feeds it a
 * live timeline view; the email step feeds it `FINAL_VIEW` for the formed map.
 */
export function CosmicMapScene({ view, className }: { view: CosmicMapView; className?: string }) {
  const { mapOpacity, focus, pan, zoom, pinShown, onLines, scanActive, scanTop } = view
  const [rootRef, w] = useElementWidth<HTMLDivElement>()

  // Width-fit: the frame spans the region's full width, so every element sits at
  // its Figma frame fraction *of the screen* — always on-screen, never side-
  // cropped. Taller regions letterbox; shorter ones crop top/bottom (the pins
  // live in the middle band). The fly-over (stage 2) zooms from this width-fit base.
  const scale = zoom
  // The camera centres the focused pin (zoomed in), or between pins drifts across
  // the overview toward the next pin (`pan`); null → centred overview / pull-back.
  const center = focus ?? pan
  const tx = center ? -(center.mx - 0.5) * scale * 100 : 0
  const ty = center ? -(center.my - 0.5) * scale * 100 : 0
  // Counter-scale for badges so pins/moon/planets/halo keep a constant on-screen size.
  const counter = 1 / scale

  return (
    <div
      ref={rootRef}
      className={cn("relative flex items-center justify-center overflow-hidden", className)}
    >
      {/* ── The frame (414×420), width-fit and cover-zoomed; the camera pans/zooms
          it. Absolutely positioned + explicitly sized so it never forces the
          region's height — the region sizes purely by flex, the frame fills it. ── */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: w ? `${w}px` : "100%",
          height: w ? `${(w * 420) / 414}px` : "100%",
          transform: `translate(-50%, -50%) translate(${tx}%, ${ty}%) scale(${scale})`,
          transition: "transform 1200ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <img
          src={AstroMap}
          alt=""
          className="absolute max-w-none"
          style={{
            left: MAP_RECT.left,
            top: MAP_RECT.top,
            width: MAP_RECT.width,
            height: MAP_RECT.height,
            opacity: mapOpacity,
          }}
        />

        {/* Stage 3: planetary-line meridians draw in over the map, one by one.
            Paths are authored in the frame's 414×420 viewBox (same space as the
            pins), so they track the continents/pins on any device. */}
        <svg
          viewBox="0 0 414 420"
          preserveAspectRatio="none"
          className="absolute inset-0 size-full"
          fill="none"
        >
          {LINES.map((line, i) => (
            <path
              key={line.d}
              d={line.d}
              stroke={line.color}
              strokeWidth={2.6}
              strokeLinecap="round"
              pathLength={1}
              strokeDasharray={1}
              style={{
                strokeDashoffset: onLines ? 0 : 1,
                opacity: onLines ? 0.95 : 0,
                filter: `drop-shadow(0 0 4px ${line.color})`,
                transition: `stroke-dashoffset 1100ms ease-out ${i * 260}ms, opacity 400ms ease-out ${i * 260}ms`,
              }}
            />
          ))}
        </svg>

        {/* Halos under the pins (stage 2): the design's soft warm spotlight (Figma
            `Ellipse 244`, Ø296 = 71.5% of the 414 frame, pin colour @20%) — a gentle
            wash that lights the globe, not a saturated bloom. One per pin, always
            mounted and opacity-gated so the active pin's halo fades in, rides across
            as the camera glides to the next pin, then fades out at the pull-back.
            Sized to the frame width and counter-scaled for a constant on-screen Ø. */}
        {PINS.map((pin) => (
          <div
            key={`halo-${pin.label}`}
            className="pointer-events-none absolute"
            style={{
              left: `${pin.mx * 100}%`,
              top: `${pin.my * 100}%`,
              transform: `translate(-50%, -50%) scale(${counter})`,
              opacity: focus?.label === pin.label ? 1 : 0,
              transition:
                "transform 1200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 800ms ease-in-out",
            }}
          >
            <div
              className="rounded-full"
              style={{
                width: w * 0.715,
                height: w * 0.715,
                background: `radial-gradient(circle, ${pin.glow}33 0%, ${pin.glow}33 52%, ${pin.glow}1f 76%, transparent 100%)`,
              }}
            />
          </div>
        ))}

        {/* Power-location pins: positioned in frame coords so they travel with the
            camera; counter-scaled to keep a constant on-screen size. */}
        {PINS.map((pin, i) => (
          <div
            key={pin.label}
            className="absolute"
            style={{
              left: `${pin.mx * 100}%`,
              top: `${pin.my * 100}%`,
              transform: `translate(-50%, -50%) scale(${counter})`,
              opacity: pinShown(i) ? 1 : 0,
              transition: "transform 1200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 500ms ease-out",
            }}
          >
            <span
              className={cn(
                "flex items-center gap-1 whitespace-nowrap rounded-full bg-dark-surface/90 px-2.5 py-1 text-[11px] font-semibold backdrop-blur",
                pin.text,
              )}
            >
              <img src={pin.icon} alt="" className="size-3.5" />
              {pin.label}
            </span>
          </div>
        ))}

        {/* Stage 3: planet badges appear one-by-one on the meridian lines. */}
        {PLANETS.map((pl, i) => (
          <div
            key={pl.sym}
            className="absolute"
            style={{
              left: `${pl.mx * 100}%`,
              top: `${pl.my * 100}%`,
              transform: `translate(-50%, -50%) scale(${counter})`,
              opacity: onLines ? 1 : 0,
              transition: `transform 1200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms ease-out ${
                onLines ? `${LINES.length * 260 + 400 + i * 180}ms` : "0ms"
              }`,
            }}
          >
            <span className="flex size-6 items-center justify-center rounded-full border border-purple-light/50 bg-dark-surface text-[12px] leading-none text-purple-light">
              {pl.sym}
            </span>
          </div>
        ))}

        {/* Stage 3: the moon settles lower-right alongside the lines. */}
        <div
          className="absolute"
          style={{
            left: `${MOON.mx * 100}%`,
            top: `${MOON.my * 100}%`,
            transform: `translate(-50%, -50%) scale(${counter})`,
            opacity: onLines ? 1 : 0,
            transition: "transform 1200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 700ms ease-out",
          }}
        >
          <img src={Moon} alt="" className="size-10" />
        </div>

        {/* Stage 1: a scan line sweeps the map once, with a glow trail above it. */}
        {scanActive && (
          <div className="pointer-events-none absolute inset-x-0" style={{ top: scanTop }}>
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-purple-light/35 to-transparent" />
            <div className="h-px bg-gradient-to-r from-transparent via-white to-transparent shadow-[0_0_14px_3px_var(--main-purple)]" />
          </div>
        )}
      </div>

      {/* Fade the map into the content area below. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-dark-base" />
    </div>
  )
}

/** A sparse, decorative starfield (CSS dots) — the constant cosmic backdrop. */
export function Starfield() {
  const stars = [
    "left-[12%] top-[10%] size-1",
    "left-[80%] top-[8%] size-1.5",
    "left-[28%] top-[20%] size-0.5",
    "left-[64%] top-[16%] size-1",
    "left-[45%] top-[6%] size-0.5",
    "left-[8%] top-[34%] size-1",
    "left-[90%] top-[30%] size-0.5",
    "left-[20%] top-[46%] size-0.5",
    "left-[72%] top-[44%] size-1",
    "left-[50%] top-[38%] size-0.5",
    "left-[34%] top-[58%] size-1",
    "left-[86%] top-[56%] size-0.5",
  ]
  return (
    <>
      {stars.map((pos) => (
        <span key={pos} className={cn("absolute rounded-full bg-purple-light/60", pos)} />
      ))}
    </>
  )
}
